import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import ImageUpload from '@/components/ImageUpload';
import RichTextEditor from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}
import { extractErrorMessage } from '@/lib/api-errors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  ArrowLeft,
  Upload,
  Save,
  Eye,
  Image,
  FileText,
  Clock,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  UserPen,
  Crown,
  Check,
  X
} from 'lucide-react';
import EditAuthorDialog from '@/components/EditAuthorDialog';
import SchedulingControls from '@/components/SchedulingControls';
import SchedulePickerDialog, { formatLisbon } from '@/components/SchedulePickerDialog';

const STORY_CATEGORIES_FALLBACK = [
  { name: 'Romance', slug: 'romance' },
  { name: 'Erótico', slug: 'erotico' },
  { name: 'Fantasia', slug: 'fantasia' },
  { name: 'Suspense', slug: 'suspense' },
  { name: 'BDSM', slug: 'bdsm' },
  { name: 'LGBT+', slug: 'lgbt' },
];

export default function AdminStoryManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [storyCategories, setStoryCategories] = useState(STORY_CATEGORIES_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  
  // Form state for creating/editing story
  const [storyForm, setStoryForm] = useState({
    title: '',
    synopsis: '',
    content: '',
    categories: [],
    tags: '',
    cover_image: '',
    is_premium: false
  });
  const [editingStory, setEditingStory] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);

  // Scheduling: staged UTC ISO to submit with the form (null = publish now)
  const [scheduledAt, setScheduledAt] = useState(null);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  // Authorship filter + bulk selection
  const [ownerFilter, setOwnerFilter] = useState('all'); // all | intimate | community | missing
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState(null); // {display_name, id} pending confirmation
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  // CSV Import state
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvPublish, setCsvPublish] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadStories();
    loadCategories();
  }, [user, navigate]);

  const loadStories = async () => {
    setLoading(true);
    try {
      const res = await api.get('/stories?limit=100&include_all=true');
      setStories(res.data);
    } catch (error) {
      toast.error('Erro ao carregar histórias');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/categories');
      const cats = Array.isArray(res.data) ? res.data : [];
      if (cats.length) {
        setStoryCategories(cats.map(c => ({ name: c.name, slug: c.slug })));
      }
    } catch (error) {
      // Keep fallback list silently
    }
  };

  const resetForm = () => {
    setStoryForm({
      title: '',
      synopsis: '',
      content: '',
      categories: [],
      tags: '',
      cover_image: '',
      is_premium: false
    });
    setEditingStory(null);
    setScheduledAt(null);
  };

  const openCreateTab = () => {
    resetForm();
    setActiveTab('create');
  };

  const openEditTab = async (story) => {
    try {
      const res = await api.get(`/stories/${story.id}`);
      const full = res.data;
      setEditingStory(full);
      setStoryForm({
        title: full.title || '',
        synopsis: full.synopsis || full.excerpt || '',
        content: full.content || '',
        categories: Array.isArray(full.categories) ? full.categories : [],
        tags: (full.tags || []).join(', '),
        cover_image: full.cover_image || '',
        is_premium: full.is_premium || false
      });
      setScheduledAt(full.scheduled_at || null);
    } catch {
      setEditingStory(story);
      setStoryForm({
        title: story.title || '',
        synopsis: story.synopsis || story.excerpt || '',
        content: story.content || '',
        categories: Array.isArray(story.categories) ? story.categories : [],
        tags: (story.tags || []).join(', '),
        cover_image: story.cover_image || '',
        is_premium: story.is_premium || false
      });
      setScheduledAt(story.scheduled_at || null);
    }
    setActiveTab('create');
  };

  const toggleCategory = (slug) => {
    setStoryForm((prev) => {
      const exists = prev.categories.includes(slug);
      return {
        ...prev,
        categories: exists ? prev.categories.filter(c => c !== slug) : [...prev.categories, slug]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!storyForm.title.trim()) {
      toast.error('Preenche o título');
      return;
    }
    if (!stripHtml(storyForm.content).trim()) {
      toast.error('Preenche o conteúdo');
      return;
    }
    if (!storyForm.categories.length) {
      toast.error('Seleciona pelo menos uma categoria');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: storyForm.title,
        synopsis: storyForm.synopsis || stripHtml(storyForm.content).slice(0, 200),
        content: storyForm.content,
        categories: storyForm.categories,
        tags: storyForm.tags.split(',').map(t => t.trim()).filter(t => t),
        cover_image: storyForm.cover_image || null,
        is_premium: !!storyForm.is_premium
      };
      if (scheduledAt) payload.scheduled_at = scheduledAt;

      if (editingStory) {
        await api.put(`/stories/${editingStory.id}`, payload);
        toast.success(scheduledAt ? `Agendada para ${formatLisbon(scheduledAt)}` : 'História atualizada');
      } else {
        await api.post('/stories', payload);
        toast.success(scheduledAt ? `Agendada para ${formatLisbon(scheduledAt)}` : 'História publicada');
      }
      
      resetForm();
      setActiveTab('list');
      loadStories();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar história'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storyId) => {
    if (!confirm('Eliminar esta história? Esta ação não pode ser revertida.')) return;
    
    try {
      await api.delete(`/stories/${storyId}`);
      toast.success('História eliminada');
      loadStories();
    } catch (error) {
      toast.error('Erro ao eliminar história');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const estimateReadingTime = (content) => {
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  // CSV Import handlers
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/admin/stories/csv-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvPreview(res.data);
      setActiveTab('import');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao processar CSV'));
    }
  };

  const handleCsvImport = async () => {
    if (!csvPreview?.stories?.length) return;
    setCsvImporting(true);
    try {
      const res = await api.post('/admin/stories/csv-import', {
        stories: csvPreview.stories,
        is_published: csvPublish,
        category: 'Leo'
      });
      toast.success(res.data.message);
      setCsvPreview(null);
      setActiveTab('list');
      loadStories();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro na importação'));
    } finally {
      setCsvImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Gestor de Histórias</h1>
                <p className="text-muted-foreground">Publicar e editar histórias</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-surface border border-border mb-6">
            <TabsTrigger value="list" className="data-[state=active]:bg-ruby">
              Lista de Histórias
            </TabsTrigger>
            <TabsTrigger value="create" className="data-[state=active]:bg-ruby">
              {editingStory ? 'Editar História' : 'Nova História'}
            </TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-ruby">
              Importar CSV
            </TabsTrigger>
          </TabsList>

          {/* Stories List */}
          <TabsContent value="list">
            {/* Filter + bulk toolbar */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
              <p className="text-muted-foreground shrink-0">{stories.length} histórias publicadas</p>
              <div className="flex-1 flex flex-wrap gap-2">
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="bg-surface border-border w-full sm:w-56" data-testid="owner-filter">
                    <SelectValue placeholder="Filtrar por autoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os autores</SelectItem>
                    <SelectItem value="intimate">Intimate / Administrador</SelectItem>
                    <SelectItem value="community">Comunidade</SelectItem>
                    <SelectItem value="missing">Sem autor válido</SelectItem>
                  </SelectContent>
                </Select>
                {selectedIds.size > 0 && (
                  <Button
                    onClick={() => {
                      setBulkOpen(true);
                      setBulkSearch('');
                      setBulkTarget(null);
                      api.get('/admin/users/authors', { params: { limit: 50 } })
                        .then(({ data }) => setBulkResults(data || []))
                        .catch(() => setBulkResults([]));
                    }}
                    variant="outline"
                    data-testid="bulk-reassign-btn"
                    className="border-ruby/50 text-ruby hover:bg-ruby/10"
                  >
                    <UserPen className="w-4 h-4 mr-2" />
                    Atribuir {selectedIds.size} conto{selectedIds.size === 1 ? '' : 's'} a autor
                  </Button>
                )}
                {selectedIds.size > 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-muted-foreground"
                  >
                    <X className="w-4 h-4 mr-1" />Limpar selecção
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setActiveTab('import')} className="border-border" data-testid="stories-csv-import-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />Importar CSV
                </Button>
                <Button onClick={openCreateTab} className="bg-ruby hover:bg-ruby-light">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova História
                </Button>
              </div>
            </div>

            {stories.length === 0 ? (
              <Card className="bg-surface border-border">
                <CardContent className="py-16 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-foreground mb-2">Nenhuma história publicada</h3>
                  <Button onClick={openCreateTab} className="bg-ruby hover:bg-ruby-light mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Publicar Primeira História
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {stories
                  .filter((s) => {
                    if (ownerFilter === 'intimate') return !s.submitted_by_community;
                    if (ownerFilter === 'community') return s.submitted_by_community;
                    if (ownerFilter === 'missing') return !s.author_id || !s.author_name;
                    return true;
                  })
                  .map((story) => {
                  const isSelected = selectedIds.has(story.id);
                  return (
                  <Card key={story.id} className={"bg-surface border-border " + (isSelected ? "ring-1 ring-ruby/50" : "")}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Selection */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setSelectedIds((prev) => {
                              const n = new Set(prev);
                              if (e.target.checked) n.add(story.id); else n.delete(story.id);
                              return n;
                            });
                          }}
                          data-testid={`select-story-${story.id}`}
                          className="w-4 h-4 accent-ruby cursor-pointer shrink-0"
                          aria-label="Selecionar conto"
                        />
                        {/* Cover */}
                        <div className="w-16 h-20 rounded overflow-hidden shrink-0 bg-white/5">
                          {story.cover_image ? (
                            <img src={story.cover_image} alt={story.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-foreground truncate">{story.title}</h3>
                            {story.is_premium && (
                              <Badge className="bg-gold/20 text-gold">Premium</Badge>
                            )}
                            {story.submitted_by_community ? (
                              <Badge variant="outline" className="border-purple-500/50 text-purple-300 bg-purple-500/10">
                                Comunidade
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-gold/50 text-gold bg-gold/10">
                                Intimate
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                            {story.excerpt || 'Sem resumo'}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                            <Badge variant="outline" className="border-border">{story.category}</Badge>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {story.reading_time || '?'} min
                            </span>
                            <span>{formatDate(story.created_at)}</span>
                            <span className="flex items-center gap-1" data-testid={`row-author-${story.id}`}>
                              <UserPen className="w-3 h-3" />
                              <strong className="text-foreground">{story.author_name || '—'}</strong>
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 shrink-0 items-center flex-wrap">
                          <SchedulingControls
                            resource="stories"
                            item={story}
                            publishedFlag={(it) => it.status === 'published'}
                            onChanged={loadStories}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/read/${story.slug || story.id}`, '_blank')}
                            className="border-border"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditTab(story)}
                            className="border-border"
                            title="Editar conto"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingAuthor(story)}
                            className="border-border"
                            title="Atribuir autor"
                            data-testid={`edit-author-${story.id}`}
                          >
                            <UserPen className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(story.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Create/Edit Story */}
          <TabsContent value="create">
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground">
                  {editingStory ? `Editar: ${editingStory.title}` : 'Nova História'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Título *</Label>
                    <Input
                      value={storyForm.title}
                      onChange={(e) => setStoryForm({...storyForm, title: e.target.value})}
                      placeholder="Título da história"
                      className="bg-background border-border"
                      data-testid="admin-story-title"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Categorias *</Label>
                    <div className="flex flex-wrap gap-2" data-testid="admin-story-categories">
                      {storyCategories.map((cat) => {
                        const selected = storyForm.categories.includes(cat.slug);
                        return (
                          <Button
                            key={cat.slug}
                            type="button"
                            variant={selected ? 'default' : 'outline'}
                            onClick={() => toggleCategory(cat.slug)}
                            className={'rounded-full ' + (selected ? 'bg-ruby hover:bg-ruby-light' : 'border-border')}
                            data-testid={`admin-story-category-${cat.slug}`}
                          >
                            {cat.name}
                          </Button>
                        );
                      })}
                    </div>
                    {storyForm.categories.length === 0 && (
                      <p className="text-xs text-muted-foreground">Seleciona uma ou mais categorias</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Sinopse</Label>
                    <Textarea
                      value={storyForm.synopsis}
                      onChange={(e) => setStoryForm({...storyForm, synopsis: e.target.value})}
                      placeholder="Breve descrição da história (será mostrada nos cards). Se deixar vazio, será gerada automaticamente do conteúdo."
                      className="bg-background border-border"
                      maxLength={300}
                      data-testid="admin-story-synopsis"
                    />
                    <p className="text-xs text-muted-foreground">{storyForm.synopsis.length}/300</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Conteúdo *</Label>
                    <RichTextEditor
                      content={storyForm.content}
                      onChange={(html) => setStoryForm({...storyForm, content: html})}
                      placeholder="Escreve a história aqui..."
                      minHeight="400px"
                    />
                    {storyForm.content && (
                      <p className="text-xs text-muted-foreground">
                        ~{stripHtml(storyForm.content).split(/\s+/).filter(Boolean).length} palavras • 
                        ~{estimateReadingTime(stripHtml(storyForm.content))} min de leitura
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageUpload
                      value={storyForm.cover_image}
                      onChange={(url) => setStoryForm({...storyForm, cover_image: url})}
                      label="Imagem de Capa"
                      aspectRatio="aspect-[2/3]"
                      recommendedSize="800×1200 · 2:3 · até 250 KB"
                      hint="Formato vertical (pôster). JPG 82% funciona bem."
                    />
                    <div className="space-y-2">
                      <Label>Tags (separadas por vírgula)</Label>
                      <Input
                        value={storyForm.tags}
                        onChange={(e) => setStoryForm({...storyForm, tags: e.target.value})}
                        placeholder="romance, sensual, Lisboa"
                        className="bg-background border-border"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <Label className="text-foreground">Conteúdo Premium</Label>
                      <p className="text-xs text-muted-foreground">
                        Apenas para subscritores premium
                      </p>
                    </div>
                    <Switch
                      checked={storyForm.is_premium}
                      onCheckedChange={(checked) => setStoryForm({...storyForm, is_premium: checked})}
                    />
                  </div>

                  <div className="flex justify-end gap-3 items-center flex-wrap">
                    {scheduledAt && (
                      <div className="text-xs text-amber-300 mr-auto bg-amber-500/10 border border-amber-500/40 rounded px-3 py-1.5" data-testid="story-scheduled-preview">
                        A guardar como agendado · {formatLisbon(scheduledAt)}
                        <button type="button" className="ml-2 underline hover:text-amber-200" onClick={() => setScheduledAt(null)} data-testid="story-clear-schedule">
                          limpar
                        </button>
                      </div>
                    )}
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        resetForm();
                        setActiveTab('list');
                      }}
                      className="border-border"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSchedulePickerOpen(true)}
                      className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                      data-testid="story-open-schedule"
                    >
                      {scheduledAt ? 'Alterar agendamento' : 'Agendar'}
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-ruby hover:bg-ruby-light" data-testid="admin-story-submit">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      {scheduledAt
                        ? (editingStory ? 'Guardar Agendamento' : 'Agendar Publicação')
                        : (editingStory ? 'Guardar Alterações' : 'Publicar História')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CSV Import Tab */}
          <TabsContent value="import">
            <Card className="bg-surface border-border">
              <CardContent className="p-6">
                {!csvPreview ? (
                  <div className="text-center py-12">
                    <FileSpreadsheet className="w-16 h-16 text-ruby/40 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">Importar Contos via CSV</h3>
                    <p className="text-muted-foreground mb-2 max-w-md mx-auto">
                      Carregue um ficheiro CSV com as colunas <strong>title</strong> e <strong>content</strong>.
                    </p>
                    <p className="text-xs text-muted-foreground mb-1 max-w-md mx-auto">
                      Todos os contos importados serão atribuídos à categoria <strong>&quot;Leo&quot;</strong>.
                    </p>
                    <p className="text-xs text-muted-foreground mb-6 max-w-md mx-auto">
                      Coluna opcional: <em>synopsis</em>. O conteúdo pode incluir HTML.
                    </p>
                    <div className="flex flex-col items-center gap-4">
                      <input type="file" accept=".csv" id="stories-csv-upload" className="hidden" onChange={handleCsvUpload} />
                      <Button onClick={() => document.getElementById('stories-csv-upload')?.click()} className="bg-ruby hover:bg-ruby-light px-8 py-5" data-testid="stories-csv-upload-btn">
                        <Upload className="w-5 h-5 mr-2" />Selecionar Ficheiro CSV
                      </Button>
                    </div>
                    <div className="mt-8 max-w-lg mx-auto text-left">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Exemplo de formato CSV:</p>
                      <pre className="bg-background border border-border rounded-lg p-4 text-xs text-muted-foreground overflow-x-auto">
{`title,content,synopsis
"Título do Conto","<p>Conteúdo em HTML...</p>","Resumo breve"
"Outro Conto","<p>Mais conteúdo...</p>",""`.trim()}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-foreground">Pré-visualização da Importação</h3>
                      <Button variant="outline" onClick={() => setCsvPreview(null)} className="border-border" size="sm">
                        <X className="w-4 h-4 mr-2" />Cancelar
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="bg-background border border-border rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-foreground">{csvPreview.total_rows}</p>
                        <p className="text-xs text-muted-foreground">Linhas no CSV</p>
                      </div>
                      <div className="bg-background border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-400">{csvPreview.valid}</p>
                        <p className="text-xs text-muted-foreground">Prontos a importar</p>
                      </div>
                      <div className="bg-background border border-red-500/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-red-400">{csvPreview.errors?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Com erros</p>
                      </div>
                    </div>
                    {csvPreview.errors?.length > 0 && (
                      <div className="mb-6 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-400 flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4" />Erros encontrados
                        </h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {csvPreview.errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-300">Linha {err.row}: {err.error}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {csvPreview.stories?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">
                          Contos a importar ({csvPreview.stories.length}) — Categoria: <span className="text-ruby">Leo</span>
                        </h4>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {csvPreview.stories.map((s, i) => (
                            <div key={i} className="flex items-center gap-3 bg-background border border-border rounded-lg p-3">
                              <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-foreground text-sm truncate">{s.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{s.synopsis}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-xs text-muted-foreground">{s.word_count} palavras</span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />{s.reading_time}m
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {csvPreview.valid > 0 && (
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={csvPublish} onChange={(e) => setCsvPublish(e.target.checked)} className="rounded" />
                          Publicar imediatamente
                        </label>
                        <Button onClick={handleCsvImport} disabled={csvImporting} className="bg-ruby hover:bg-ruby-light px-8" data-testid="stories-csv-confirm-btn">
                          {csvImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          Importar {csvPreview.valid} Contos
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <EditAuthorDialog
        open={!!editingAuthor}
        onOpenChange={(o) => !o && setEditingAuthor(null)}
        story={editingAuthor || {}}
        onSaved={(updated) => {
          setStories((arr) => arr.map((it) => it.id === updated.id ? { ...it, ...updated } : it));
        }}
      />

      <SchedulePickerDialog
        open={schedulePickerOpen}
        onOpenChange={setSchedulePickerOpen}
        description={storyForm.title || (editingStory ? editingStory.title : 'Nova História')}
        initialIso={scheduledAt}
        confirmLabel="Confirmar agendamento"
        onConfirm={async (iso) => {
          setScheduledAt(iso);
          setSchedulePickerOpen(false);
        }}
      />

      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkTarget(null); setBulkSearch(''); } }}>
        <DialogContent className="bg-surface border-border sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPen className="w-5 h-5 text-ruby" />
              Atribuir contos a autor
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Vais atribuir <strong className="text-foreground">{selectedIds.size}</strong> conto{selectedIds.size === 1 ? '' : 's'} ao mesmo autor.
            Passa a aparecer no perfil dele e na secção da comunidade (ou reverte para Intimate/Administrador).
          </p>

          <div className="rounded-lg border border-border bg-background/40 p-3 mb-3">
            <p className="text-xs text-muted-foreground mb-1">Contos seleccionados</p>
            <ul className="text-sm text-foreground space-y-0.5 max-h-24 overflow-y-auto">
              {stories.filter((s) => selectedIds.has(s.id)).slice(0, 8).map((s) => (
                <li key={s.id} className="truncate">• {s.title} <span className="text-muted-foreground text-xs">(atualmente: {s.author_name})</span></li>
              ))}
              {selectedIds.size > 8 && (
                <li className="text-muted-foreground text-xs italic">… e mais {selectedIds.size - 8}</li>
              )}
            </ul>
          </div>

          <div className="relative mb-2">
            <Input
              value={bulkSearch}
              onChange={(e) => {
                setBulkSearch(e.target.value);
                api.get('/admin/users/authors', { params: { search: e.target.value, limit: 50 } })
                  .then(({ data }) => setBulkResults(data || []))
                  .catch(() => {});
              }}
              placeholder="Procurar autor…"
              className="bg-background border-border"
              data-testid="bulk-search-input"
            />
          </div>

          <div className="max-h-52 overflow-y-auto rounded-lg border border-border bg-background/30 divide-y divide-border/60 mb-3">
            <button
              type="button"
              onClick={() => setBulkTarget({ id: '', display_name: 'Intimate / Administrador' })}
              className={"w-full text-left p-3 hover:bg-gold/10 flex items-center gap-2 " + (bulkTarget?.id === '' ? 'bg-gold/10' : '')}
              data-testid="bulk-pick-admin"
            >
              <Crown className="w-4 h-4 text-gold" />
              <span className="text-foreground">Voltar a Intimate / Administrador</span>
            </button>
            {bulkResults.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setBulkTarget({ id: u.id, display_name: u.display_name })}
                data-testid={`bulk-pick-${u.id}`}
                className={"w-full text-left p-3 hover:bg-ruby/10 flex items-center gap-2 " + (bulkTarget?.id === u.id ? 'bg-ruby/10' : '')}
              >
                <UserPen className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-foreground font-medium truncate">{u.display_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email} · {u.story_count} contos</div>
                </div>
                {bulkTarget?.id === u.id && <Check className="w-4 h-4 text-ruby" />}
              </button>
            ))}
          </div>

          {bulkTarget && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-100 p-3 text-sm">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Vais aplicar <strong>{bulkTarget.display_name}</strong> a <strong>{selectedIds.size}</strong> conto{selectedIds.size === 1 ? '' : 's'}. Confirma para continuar.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="border-border">Cancelar</Button>
            <Button
              disabled={!bulkTarget || bulkSaving}
              onClick={async () => {
                setBulkSaving(true);
                try {
                  const { data } = await api.post('/admin/stories/reassign-bulk', {
                    story_ids: Array.from(selectedIds),
                    author_id: bulkTarget.id,
                  });
                  toast.success(`${data.updated} conto(s) atribuído(s) a ${data.new_author_name}`);
                  if (data.failed?.length) toast.error(`${data.failed.length} conto(s) falharam`);
                  loadStories();
                  setSelectedIds(new Set());
                  setBulkOpen(false);
                  setBulkTarget(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Erro na atribuição em lote');
                } finally {
                  setBulkSaving(false);
                }
              }}
              className="bg-ruby hover:bg-ruby-light"
              data-testid="bulk-confirm-btn"
            >
              {bulkSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar atribuição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
